import { Component, input } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideArrowDown, lucideArrowUp, lucideChevronsUpDown } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { type HeaderContext, injectFlexRenderContext } from '@tanstack/angular-table';
import type { ColumnAlign } from '../types';

@Component({
  selector: 'au-sort-button',
  imports: [HlmButtonImports, NgIcon],
  providers: [provideIcons({ lucideChevronsUpDown, lucideArrowUp, lucideArrowDown })],
  host: { class: 'flex' },
  template: `
    <button
      class="w-full px-0"
      [class.!justify-end]="align() === 'right'"
      [class.!justify-start]="align() !== 'right'"
      [class.tabular-nums]="align() === 'right'"
      hlmBtn
      variant="ghost"
      size="sm"
      (click)="toggleSort()"
    >
      @if (align() === 'right') {
        <!-- Measure column: icon to the LEFT of the label -->
        <ng-icon
          class="mr-1"
          [name]="sortIcon()"
        />
      }
      {{ label() }}
      @if (align() !== 'right') {
        <!-- Dimension column: icon to the RIGHT of the label -->
        <ng-icon
          class="ml-1"
          [name]="sortIcon()"
        />
      }
    </button>
  `,
})
export class SortButton {
  readonly label = input.required<string>();
  /**
   * Aligns the sort button content. When `'right'`, switches the button's
   * flex-justification to `justify-end` so label+icon sit on the right edge.
   * When absent or `'left'`, keeps the default `justify-start`.
   *
   * Side rule (design D5):
   * - Left-aligned columns (dimensions): icon renders to the RIGHT of the label.
   * - Right-aligned columns (measures): icon renders to the LEFT of the label,
   *   and `tabular-nums` is applied for numeric alignment.
   *
   * Two coexisting header-alignment paths:
   * - String/non-sortable headers: inner-`<div>` `text-right` (via `isHeaderRightAligned`)
   * - Sortable headers: this `SortButton` `justify-end` (via this `align` input)
   * Both are driven by `colDef.align`; `applySortHeaders` forwards it here.
   *
   * Note: the template's `!justify-*` important-prefix is required — hlmBtn's CVA base
   * sets `justify-center`, so a plain `justify-*` utility would lose the cascade. Keep
   * the prefix if this button's variant/size changes.
   */
  readonly align = input<ColumnAlign>();
  private readonly context = injectFlexRenderContext<HeaderContext<unknown, unknown>>();
  protected readonly sortIcon = () => {
    const sort = this.context.column.getIsSorted();
    if (sort === 'asc') return 'lucideArrowUp';
    if (sort === 'desc') return 'lucideArrowDown';
    return 'lucideChevronsUpDown';
  };

  toggleSort() {
    this.context.column.toggleSorting();
  }
}
